import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { Button } from "src/@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem
} from "src/@/components/ui/select";
import { Card, CardHeader, CardContent, CardFooter } from "src/@/components/ui/card";
import { Textarea } from "src/@/components/ui/textarea";
import useAuth from "src/hooks/useAuth";
import actionListaCanaisPorCliente from "src/actions/clientes/actionListaCanaisPorCliente";
import { OptionsSelect } from "src/@/components/shared";
import { UploadCampanha } from "src/components/upload";
import MicRecorder from "mic-recorder-to-mp3";
import { CircularProgress, Divider, IconButton, LinearProgress } from "@mui/material";
import Iconify from "src/components/Iconify";
import RecordingTimer from "src/components/_chats/chat/components/RecordingTimer";
import v2_actionUploadS3 from "src/actions/v2/global/actionUploadS3";
import { FileListItem } from "src/@/components/shared/FileListItem";
import { FileListItemItemText } from "src/@/components/shared/FileListItemItemText";
import UploadAutomacoes from "src/components/upload/UploadAutomacoes";
import { Alert } from "src/@/components/ui/alert";
import { AlertInfo } from "src/@/components/shared/AlertInfo";
import { Switch } from "src/@/components/ui/switch";
import { RowInput } from "src/components/forms/RowInput";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "src/@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/@/components/ui/popover";
import PhoneInput from 'react-phone-number-input';
import { PopoverVariaveis } from "../components/PopoverVariaveis";
import ModalMensagensRapidasAutomacoes from "../components/ModalMensagensRapidasAutomacoes";
import { Input } from "src/@/components/ui/input";
import { Label } from "src/@/components/ui/label";
import { transformCategoryTemplateWhatsApp } from "src/utils/meta.utils";
// actions
import v1_actionWboTemplates from "src/actions/v1/meta/wbo/v1_actionWboTemplates";
import VariableInput from "../components/VariableInput";

const CardSendMessage = ({ block, changeBlock, actionState, actionPhase, isChatFlow, availableVariables }) => {
  const { cliente, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('automation');
  const [canais, setCanais] = useState([]);
  const prevIsWhatsAppBusinessRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [audioGravado, setAudioGravado] = useState(null);
  const [tempoRestante, setTempoRestante] = useState(180);
  const [mostrarAlerta, setMostrarAlerta] = useState(false);
  const recorder = useMemo(() => new MicRecorder({ bitRate: 128 }), []);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isEditFile, setIsEditFile] = useState(false);
  const [files, setFiles] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [filesEdit, setFilesEdit] = useState(block?.data?.chatFiles || []);
  const [loadingEnviarArquivos, setLoadingEnviarArquivos] = useState(false);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  let onlyMsgText = actionPhase?.value === "from.chats.resume";

  const normalizarComponents = (components) => {
    if (!Array.isArray(components)) return [];
    return components.map((c) => ({
      ...c,
      type: typeof c.type === 'string' ? c.type.toUpperCase() : c.type,
    }));
  };

  const extrairVariaveisTemplate = (components) => {
    const variaveis = {};
    if (!components || !Array.isArray(components)) {
      return variaveis;
    }
    components.forEach((component) => {
      if (component.text) {
        const matches = component.text.match(/\{\{(\d+)\}\}/g);
        if (matches) {
          matches.forEach((match) => {
            const numero = match.replace(/\{\{|\}\}/g, '');
            variaveis[numero] = '';
          });
        }
      }
      if (component.buttons && Array.isArray(component.buttons)) {
        component.buttons.forEach((button) => {
          if (button.text) {
            const matches = button.text.match(/\{\{(\d+)\}\}/g);
            if (matches) {
              matches.forEach((match) => {
                const numero = match.replace(/\{\{|\}\}/g, '');
                variaveis[numero] = '';
              });
            }
          }
        });
      }
    });
    return variaveis;
  };

  const handleSelecionarTemplate = (templateId) => {
    if (!templateId || templateId === "none") {
      changeBlock({ name: "templateSelecionado", value: null });
      changeBlock({ name: "templateVariaveis", value: {} });
      return;
    }
    const template = templates.find(t => (t.templateId || t._id) === templateId);
    if (!template) return;
    const comps = normalizarComponents(template?.components || []);
    const conteudo = comps?.find(c => c.type === 'BODY')?.text || '';
    const variaveis = extrairVariaveisTemplate(comps);
    changeBlock({
      name: "templateSelecionado",
      value: {
        templateId: template.templateId || template._id,
        name: template.name || template.templateName,
        conteudo: conteudo,
        components: comps
      }
    });
    changeBlock({ name: "templateVariaveis", value: variaveis });
  };

  const handleAtualizarVariavelTemplate = (variavel, valor) => {
    const variaveisAtuais = block?.data?.templateVariaveis || {};
    changeBlock({
      name: "templateVariaveis",
      value: { ...variaveisAtuais, [variavel]: valor }
    });
  };

  const handleInserirVariavelNoCampo = (variavelKey, variavelNome) => {
    const valorAtual = block?.data?.templateVariaveis?.[variavelKey] || '';
    const novoValor = valorAtual + variavelNome;
    handleAtualizarVariavelTemplate(variavelKey, novoValor);
  };

  const variaveisDisponiveis = [
    { name: '{{nome}}', desc: t('sendMessage.variables.chatName', 'Nome do chat') },
    { name: '{{numero}}', desc: t('sendMessage.variables.chatNumber', 'Número do chat') },
    { name: '{{nota}}', desc: t('sendMessage.variables.chatRating', 'Nota de avaliação do chat') }
  ];

  const substituirVariaveis = (texto, variaveis) => {
    if (!texto || !variaveis) return texto;
    let textoSubstituido = texto;
    Object.keys(variaveis).forEach((key) => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      textoSubstituido = textoSubstituido.replace(regex, variaveis[key] || `{{${key}}}`);
    });
    return textoSubstituido;
  };

  const criarPreviewComCamposTemplate = (texto, variaveis) => {
    if (!texto) return null;
    const partes = texto.split(/(\{\{\d+\}\})/g);
    return partes.map((parte, index) => {
      const match = parte.match(/\{\{(\d+)\}\}/);
      if (match) {
        const numeroVariavel = match[1];
        const valorVariavel = variaveis[numeroVariavel] || '';
        return (
          <span key={index} className="inline-block">
            {valorVariavel ? (
              <span className="inline-block px-2 py-1 mx-1 bg-purple-100 text-purple-800 rounded border border-purple-300 font-medium">
                {valorVariavel}
              </span>
            ) : (
              <span className="inline-block px-2 py-1 mx-1 bg-purple-50 text-purple-600 rounded border-2 border-dashed border-purple-300 italic">
                {`{{${numeroVariavel}}}`}
              </span>
            )}
          </span>
        );
      }
      return <span key={index} className="whitespace-pre-wrap">{parte}</span>;
    });
  };

  const opcoesUnidadeTempo = [
    { value: 'minutos', label: t('sendMessage.scheduleUnit.minutes', 'Minutos') },
    { value: 'horas', label: t('sendMessage.scheduleUnit.hours', 'Horas') },
    { value: 'dias', label: t('sendMessage.scheduleUnit.days', 'Dias') },
  ];

  const optionsMessage = [
    { value: "text", label: t('sendMessage.messageType.text', 'Texto'), icon: "lets-icons:message-duotone" },
    { value: "file", label: t('sendMessage.messageType.file', 'Arquivo'), icon: "solar:file-linear" },
    { value: "audio", label: t('sendMessage.messageType.audio', 'Audio'), icon: "mingcute:voice-fill" },
    { value: "image", label: t('sendMessage.messageType.image', 'Imagem'), icon: "material-symbols:image-outline" },
    { value: "resume", label: t('sendMessage.messageType.resume', 'Resumo'), icon: "fe:magic" },
    { value: "reengagement", label: t('sendMessage.messageType.reengagement', 'Reengajamento'), icon: "fluent:chat-sparkle-24-regular" }
  ];

  const isWhatsAppBusiness = useMemo(() => {
    if (!canais || canais.length === 0) {
      return false;
    }
    if (block?.data?.useChannel && block?.data?.chatChannel) {
      const canalId = block.data.chatChannel;
      const canalSelecionadoBloco = canais.find(c => {
        const cId = c._id || c.id;
        return cId === canalId;
      });
      if (canalSelecionadoBloco) {
        return canalSelecionadoBloco.platform === 'whatsapp_business';
      }
      return false;
    }
    const verificarCanaisWhatsAppBusiness = (idsCanais) => {
      if (!idsCanais || !Array.isArray(idsCanais) || idsCanais.length === 0) {
        return false;
      }
      const idsNormalizados = idsCanais.map(ch => typeof ch === 'string' ? ch : ch.cliente_id);
      const canaisEncontrados = canais.filter(c => {
        const canalId = c.cliente_id;
        return idsNormalizados.includes(canalId) && c.platform === 'whatsapp_business';
      });
      return canaisEncontrados.length > 0;
    };
    if (actionPhase?.chatChannels) {
      if (verificarCanaisWhatsAppBusiness(actionPhase.chatChannels)) {
        return true;
      }
    }
    if (actionState?.chatChannels) {
      if (verificarCanaisWhatsAppBusiness(actionState.chatChannels)) {
        return true;
      }
    }
    return false;
  }, [canais, actionPhase?.chatChannels, actionState?.chatChannels, block?.data?.chatChannel, block?.data?.useChannel]);

  console.log('isWhatsAppBusiness', isWhatsAppBusiness);

  const templateObrigatorio = useMemo(() => {
    return isWhatsAppBusiness && ["text", "reengagement"].includes(block?.data?.chatTypeMessage);
  }, [isWhatsAppBusiness, block?.data?.chatTypeMessage]);

  const temWhatsAppBusinessEmChatChannels = useMemo(() => {
    if (!canais || canais.length === 0) {
      return false;
    }
    if (block?.data?.useChannel && block?.data?.chatChannel) {
      return false;
    }
    const verificarCanaisWhatsAppBusiness = (idsCanais) => {
      if (!idsCanais || !Array.isArray(idsCanais) || idsCanais.length === 0) {
        return false;
      }
      const idsNormalizados = idsCanais.map(ch => typeof ch === 'string' ? ch : ch.cliente_id);
      const canaisEncontrados = canais.filter(c => {
        const canalId = c.cliente_id;
        return idsNormalizados.includes(canalId) && c.platform === 'whatsapp_business';
      });
      return canaisEncontrados.length > 0;
    };
    if (actionPhase?.chatChannels) {
      if (verificarCanaisWhatsAppBusiness(actionPhase.chatChannels)) {
        return true;
      }
    }
    if (actionState?.chatChannels) {
      if (verificarCanaisWhatsAppBusiness(actionState.chatChannels)) {
        return true;
      }
    }
    return false;
  }, [canais, actionPhase?.chatChannels, actionState?.chatChannels, block?.data?.useChannel, block?.data?.chatChannel]);

  const optionsMessageFiltradas = useMemo(() => {
    let opcoesFiltradas = optionsMessage;
    if (isWhatsAppBusiness) {
      opcoesFiltradas = opcoesFiltradas.filter(option => option.value !== "audio");
    }
    if (temWhatsAppBusinessEmChatChannels) {
      opcoesFiltradas = opcoesFiltradas.filter(option => option.value !== "resume");
    }
    return opcoesFiltradas;
  }, [isWhatsAppBusiness, temWhatsAppBusinessEmChatChannels]);

  const temMaisDeUmWhatsAppBusiness = useMemo(() => {
    if (!canais || canais.length === 0) {
      return false;
    }
    const normalizarIdsCanais = (channels) => {
      if (!channels || !Array.isArray(channels) || channels.length === 0) {
        return [];
      }
      return channels.map(ch => {
        if (typeof ch === 'string') return String(ch);
        if (typeof ch === 'object' && ch !== null) return String(ch._id || ch.id || ch);
        return String(ch);
      });
    };
    let temWhatsAppBusinessViaUseChannel = false;
    if (block?.data?.useChannel && block?.data?.chatChannel) {
      const canalId = block.data.chatChannel;
      const canalSelecionadoBloco = canais.find(c => {
        const cId = c.cliente_id;
        return cId === canalId;
      });
      if (canalSelecionadoBloco && canalSelecionadoBloco.platform === 'whatsapp_business') {
        temWhatsAppBusinessViaUseChannel = true;
      }
    }
    let idsCanaisSelecionados = [];
    if (actionPhase?.chatChannels && Array.isArray(actionPhase.chatChannels) && actionPhase.chatChannels.length > 0) {
      idsCanaisSelecionados = normalizarIdsCanais(actionPhase.chatChannels);
    } else if (actionState?.chatChannels && Array.isArray(actionState.chatChannels) && actionState.chatChannels.length > 0) {
      idsCanaisSelecionados = normalizarIdsCanais(actionState.chatChannels);
    }
    if (temWhatsAppBusinessViaUseChannel && idsCanaisSelecionados.length > 0) {
      console.log('⚠️ Validação: Há canal WhatsApp Business via useChannel mas também há canais em chatChannels', {
        canalUseChannel: block?.data?.chatChannel,
        canaisChatChannels: idsCanaisSelecionados
      });
      return true;
    }
    if (idsCanaisSelecionados.length === 0) {
      return false;
    }
    const canaisWhatsAppBusiness = canais.filter(c => {
      const canalId = String(c.cliente_id);
      return idsCanaisSelecionados.includes(canalId) && c.platform === 'whatsapp_business';
    });
    if (canaisWhatsAppBusiness.length > 1) {
      console.log('⚠️ Validação: Mais de 1 canal WhatsApp Business detectado', {
        canaisSelecionados: idsCanaisSelecionados,
        canaisWhatsAppBusiness: canaisWhatsAppBusiness.map(c => ({
          id: c.cliente_id,
          nome: c.nome,
          platform: c.platform
        })),
        total: canaisWhatsAppBusiness.length
      });
      return true;
    }
    if (canaisWhatsAppBusiness.length === 1) {
      if (idsCanaisSelecionados.length > 1) {
        console.log('⚠️ Validação: Há 1 canal WhatsApp Business mas também há outros canais selecionados', {
          canaisSelecionados: idsCanaisSelecionados,
          canalWhatsAppBusiness: canaisWhatsAppBusiness[0],
          totalCanaisSelecionados: idsCanaisSelecionados.length
        });
        return true;
      }
    }
    return false;
  }, [canais, actionPhase?.chatChannels, actionState?.chatChannels, block?.data?.useChannel, block?.data?.chatChannel]);

  const naoTemCanaisSelecionados = useMemo(() => {
    if (isChatFlow) return false;
    if (block?.data?.useChannel && block?.data?.chatChannel) {
      return false;
    }
    const temCanaisPhase = actionPhase?.chatChannels && Array.isArray(actionPhase.chatChannels) && actionPhase.chatChannels.length > 0;
    const temCanaisState = actionState?.chatChannels && Array.isArray(actionState.chatChannels) && actionState.chatChannels.length > 0;
    return !temCanaisPhase && !temCanaisState;
  }, [block?.data?.useChannel, block?.data?.chatChannel, actionPhase?.chatChannels, actionState?.chatChannels, actionPhase?.value, isChatFlow]);

  const buscarListaCanais = async () => {
    try {
      const response = await actionListaCanaisPorCliente(cliente._id);
      setCanais(response.data.data);
    } catch (error) {
      console.log(error);
    }
  };

  const handleDropMultiFile = useCallback(
    async (acceptedFiles) => {
      try {
        const response = await uploadAndGetUrl(acceptedFiles);
        let event = { name: "chatFiles", value: response };
        changeBlock(event);
      } catch (error) {
        console.log('erro ao fazer upload do arquivo:', error);
      }
      setFiles(acceptedFiles.map((file) =>
        Object.assign(file, { preview: URL.createObjectURL(file) })
      ));
    },
    [setFiles]
  );

  const handleRemove = (file) => {
    let filesBlock = block?.data?.chatFiles || [];
    const filteredItems = filesBlock.filter((_file) => _file.key !== file.key);
    console.log('filteredItems', filteredItems);
    let event = { name: "chatFiles", action: "delete", value: filteredItems };
    changeBlock(event);
    setFiles(filteredItems);
  };

  const handleRemoveEdit = (file) => {
    const filteredItems = filesEdit.filter((_file) => _file !== file);
    setIsEditFile(false);
    setFilesEdit(filteredItems);
  };

  const handleStartRecording = async () => {
    setLoadingAudio(true);
    setTempoRestante(180);
    setMostrarAlerta(false);
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      await recorder.start();
      setRecording(true);
      setLoadingAudio(false);
      const timer = setInterval(() => {
        setTempoRestante((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            pararGravacao();
            return 0;
          }
          if (prev <= 30) {
            setMostrarAlerta(true);
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    } catch (err) {
      console.log(err);
      setLoadingAudio(false);
    }
  };

  async function pararGravacao() {
    try {
      const [buffer, blob] = await recorder.stop().getMp3();
      const file = new File(buffer, "audio.mp3", {
        type: blob.type,
        lastModified: Date.now()
      });
      setAudioGravado(file);
      setRecording(false);
      const audioUpload = await uploadAudioAndGetUrl(file);
      if (audioUpload) {
        let event = { name: "chatAudioMessage", value: audioUpload };
        changeBlock(event);
      }
    } catch (error) {
      // filesUrl = [];
    }
  }

  async function uploadAndGetUrl(files) {
    let filesUrlPush = [];
    setLoadingUpload(true);
    if (files.length > 0) {
      for (let file of files) {
        var formData = new FormData();
        var imagefile = file;
        var filename = file.name;
        formData.append("file", imagefile);
        formData.append("filename", filename);
        try {
          let querys = `?path=plataforma/automacoes/${cliente._id}`;
          const response = await v2_actionUploadS3(formData, querys);
          filesUrlPush.push({
            key: response.data.data.key,
            type: file.type,
            location: response.data.data.location,
            name: file.name
          });
        } catch (error) {
          console.log('Erro ao fazer upload do arquivo:', error);
        }
      }
    }
    setLoadingUpload(false);
    return filesUrlPush;
  }

  async function uploadAudioAndGetUrl(file) {
    if (file) {
      var formData = new FormData();
      var audiofile = file;
      formData.append("file", audiofile);
      try {
        let querys = `?path=plataforma/automacoes`;
        const response = await v2_actionUploadS3(formData, querys);
        if (!response.data.error) {
          return {
            key: response.data.data.key,
            type: file.type,
            tipo: "audio",
            location: response.data.data.location
          };
        }
      } catch (error) {
        console.log('erro ao upload arquivo', error);
      }
    }
  }

  const handleCancelAudio = async () => {
    try {
      await recorder.stop().getMp3();
      setRecording(false);
      setAudioGravado(null);
    } catch (err) {
      console.log(err);
    }
  };

  const buscarTemplates = useCallback(async () => {
    if (!user?.clienteId) return;
    try {
      setLoadingTemplates(true);
      let canalId = null;
      if (block?.data?.useChannel && block?.data?.chatChannel) {
        canalId = block.data.chatChannel;
      } else if (actionPhase?.chatChannels && Array.isArray(actionPhase.chatChannels) && actionPhase.chatChannels.length > 0) {
        const idsCanais = actionPhase.chatChannels.map(ch => typeof ch === 'string' ? ch : ch.cliente_id);
        const canalWhatsAppBusiness = canais.find(c => {
          const cId = c.cliente_id;
          return idsCanais.includes(cId) && c.platform === 'whatsapp_business';
        });
        if (canalWhatsAppBusiness) {
          canalId = canalWhatsAppBusiness.cliente_id;
        }
      } else if (actionState?.chatChannels && Array.isArray(actionState.chatChannels) && actionState.chatChannels.length > 0) {
        const idsCanais = actionState.chatChannels.map(ch => typeof ch === 'string' ? ch : ch.cliente_id);
        const canalWhatsAppBusiness = canais.find(c => {
          const cId = c.cliente_id;
          return idsCanais.includes(cId) && c.platform === 'whatsapp_business';
        });
        if (canalWhatsAppBusiness) {
          canalId = canalWhatsAppBusiness.cliente_id;
        }
      }
      const res = await v1_actionWboTemplates(canalId);
      setTemplates(res.data?.data?.templates || []);
    } catch (error) {
      console.log('Erro ao buscar templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  }, [user?.clienteId, block?.data?.useChannel, block?.data?.chatChannel, actionPhase?.chatChannels, actionState?.chatChannels, canais]);

  useEffect(() => {
    buscarListaCanais();
  }, []);

  useEffect(() => {
    const tiposPermitemTemplate = ["text", "file", "image", "reengagement"];
    if (tiposPermitemTemplate.includes(block?.data?.chatTypeMessage) && block?.data?.useTemplate) {
      buscarTemplates();
    }
  }, [block?.data?.chatTypeMessage, block?.data?.useTemplate, buscarTemplates]);

  useEffect(() => {
    if (["text", "reengagement"].includes(block?.data?.chatTypeMessage)) {
      if (templateObrigatorio && !block?.data?.useTemplate) {
        changeBlock({ name: "useTemplate", value: true });
        buscarTemplates();
      } else if (!templateObrigatorio && block?.data?.useTemplate && isWhatsAppBusiness) {
        changeBlock({ name: "useTemplate", value: false });
        changeBlock({ name: "templateSelecionado", value: null });
        changeBlock({ name: "templateVariaveis", value: {} });
      }
    }
  }, [templateObrigatorio, block?.data?.chatTypeMessage, block?.data?.useTemplate, isWhatsAppBusiness]);

  useEffect(() => {
    if (isWhatsAppBusiness && block?.data?.chatTypeMessage === 'audio') {
      changeBlock({ name: 'chatTypeMessage', value: null });
      changeBlock({ name: 'chatAudioMessage', value: null });
    }
  }, [isWhatsAppBusiness, block?.data?.chatTypeMessage]);

  useEffect(() => {
    if (prevIsWhatsAppBusinessRef.current === null) {
      prevIsWhatsAppBusinessRef.current = isWhatsAppBusiness;
    }
  }, []);

  useEffect(() => {
    const prevValue = prevIsWhatsAppBusinessRef.current;
    if (prevValue === true && isWhatsAppBusiness === false) {
      changeBlock({ name: 'useTemplate', value: false });
      changeBlock({ name: 'templateSelecionado', value: null });
      changeBlock({ name: 'templateVariaveis', value: {} });
    }
    prevIsWhatsAppBusinessRef.current = isWhatsAppBusiness;
  }, [isWhatsAppBusiness]);

  useEffect(() => {
    if (temWhatsAppBusinessEmChatChannels && block?.data?.chatTypeMessage === 'resume') {
      changeBlock({ name: 'chatTypeMessage', value: null });
    }
  }, [temWhatsAppBusinessEmChatChannels, block?.data?.chatTypeMessage]);

  if (naoTemCanaisSelecionados) {
    return (
      <div className="w-full">
        <Alert className="border-red-500 bg-red-50">
          <div className="flex items-start gap-3 w-full">
            <Iconify icon="mdi:alert-circle" className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-900 mb-1">
                {t('sendMessage.errors.noChannelsTitle', 'Canais não selecionados')}
              </h4>
              <p className="text-sm text-red-800">
                {t('sendMessage.errors.noChannelsBody', 'É necessário selecionar pelo menos um canal para continuar. Por favor, ajuste a configuração dos canais da ação.')}
              </p>
            </div>
          </div>
        </Alert>
      </div>
    );
  }

  if (temMaisDeUmWhatsAppBusiness) {
    return (
      <div className="w-full">
        <Alert className="border-red-500 bg-red-50">
          <div className="flex items-start gap-3 w-full">
            <Iconify icon="mdi:alert-circle" className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-900 mb-1">
                {t('sendMessage.errors.invalidChannelsTitle', 'Configuração inválida de canais')}
              </h4>
              <p className="text-sm text-red-800 mb-2">
                {t('sendMessage.errors.invalidChannelsBodyPrefix', 'Quando um canal de WhatsApp Business está selecionado, pode haver')} <strong>{t('sendMessage.errors.invalidChannelsBodyStrong', 'somente 1 canal selecionado')}</strong>.
              </p>
              <p className="text-sm text-red-800">
                {t('sendMessage.errors.invalidChannelsBodyFooter', 'Por favor, ajuste a configuração dos canais da ação para que haja apenas um canal WhatsApp Business selecionado, sem outros canais adicionais.')}
              </p>
            </div>
          </div>
        </Alert>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="space-y-4 flex flex-col gap-4">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2 mt-4">
            <Switch
              checked={block?.data?.useChannel}
              onCheckedChange={(checked) => changeBlock({ name: "useChannel", value: checked })}
            />
            <div className="flex items-center gap-1">
              <label className="text-sm font-medium">{t('sendMessage.channel.useSpecificLabel', 'Selecionar canal específico')}</label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="flex items-center cursor-help">
                      <Iconify icon="mdi:information-outline" width={18} className="text-slate-500" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" align="start" className="max-w-[280px]">
                    <p className="text-xs">
                      {t('sendMessage.channel.useSpecificTooltip', 'Quando ativado, você pode selecionar um canal específico para enviar a mensagem. Se desativado, a mensagem será enviada através dos canais configurados na ação. Ao selecionar um canal específico, as regras de template podem variar dependendo do tipo de canal escolhido.')}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          {block?.data?.useChannel && (
            <div className="mt-2">
              <label className="block text-sm font-medium">{t('sendMessage.channel.selectLabel', 'Canal de Envio')}</label>
              <Select
                value={block?.data?.chatChannel || undefined}
                onValueChange={e => {
                  let event = { name: "chatChannel", value: e === "none" ? null : e };
                  changeBlock(event);
                }}
                className="w-full"
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('sendMessage.channel.selectPlaceholder', 'Selecione um canal')} />
                </SelectTrigger>
                <SelectContent>
                  {canais.map((channel) => (
                    <SelectItem key={channel._id} value={channel._id}>
                      {channel.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <Divider className="my-4 border-dashed" />

          {!onlyMsgText && (
            <>
              <OptionsSelect
                title={t('sendMessage.messageType.title', 'Tipo de mensagem')}
                options={optionsMessageFiltradas}
                value={block?.data?.chatTypeMessage}
                changeValue={(e) => {
                  let event = { name: "chatTypeMessage", value: e };
                  changeBlock(event);
                  if (e === 'resume') {
                    changeBlock({ name: 'sendDirectNumber', value: true });
                  }
                  if (block?.data?.chatTypeMessage === 'audio' && e !== 'audio' && isWhatsAppBusiness) {
                    changeBlock({ name: 'chatAudioMessage', value: null });
                  }
                }}
              />
            </>
          )}

          {block?.data?.chatTypeMessage === "text" && (
            <div className="flex flex-row items-center gap-2">
              <ModalMensagensRapidasAutomacoes
                onSelect={(e) => {
                  let event = { name: "chatTextMessage", value: e };
                  changeBlock(event);
                }}
              />
              {!isChatFlow && (
                <PopoverVariaveis />
              )}
            </div>
          )}

          {block?.data?.chatTypeMessage === "image" && (
            <div>
              <UploadAutomacoes
                loadingEnviarArquivos={loadingEnviarArquivos}
                showPreview={false}
                files={files}
                isEdit={isEdit}
                isEditFile={isEditFile}
                filesEdit={filesEdit}
                onRemoveEdit={handleRemoveEdit}
                onDrop={handleDropMultiFile}
                onRemove={handleRemove}
              />
              {block?.data?.chatFiles?.map((file, index) => (
                <div key={index} className="flex flex-col gap-2 mt-4">
                  <FileListItemItemText
                    key={index}
                    file={file}
                    name={file.name}
                    onRemove={handleRemove}
                  />
                </div>
              ))}
            </div>
          )}

          {block?.data?.chatTypeMessage === "file" && (
            <div>
              <UploadAutomacoes
                loadingEnviarArquivos={loadingEnviarArquivos}
                showPreview={false}
                files={files}
                isEdit={isEdit}
                isEditFile={isEditFile}
                filesEdit={filesEdit}
                onRemoveEdit={handleRemoveEdit}
                onDrop={handleDropMultiFile}
                onRemove={handleRemove}
              />
              {block?.data?.chatFiles?.map((file, index) => (
                <div key={index} className="flex flex-col gap-2 mt-4">
                  <FileListItemItemText
                    key={index}
                    file={file}
                    name={file.name}
                    onRemove={handleRemove}
                  />
                </div>
              ))}
            </div>
          )}

          {block?.data?.chatTypeMessage === "reengagement" && (
            <div className="mt-4 flex flex-col gap-3">
              <div className="p-4 rounded-xl bg-violet-50/60 border border-violet-100">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0">
                    <Iconify icon="fluent:chat-sparkle-24-regular" width={18} className="text-[#7C3AED]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-violet-900 mb-1">
                      {t('sendMessage.reengagement.title', 'Mensagem de reengajamento gerada por IA')}
                    </p>
                    <p className="text-xs text-violet-800/80 leading-relaxed">
                      {t('sendMessage.reengagement.description', 'A IA irá analisar o histórico da conversa e gerar uma mensagem natural para retomar o contato com o cliente, no mesmo tom do atendimento.')}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('sendMessage.reengagement.instructionsLabel', 'Instruções para a IA')}
                  <span className="text-gray-400 ml-1 font-normal">{t('sendMessage.reengagement.optionalSuffix', '(opcional)')}</span>
                </label>
                <Textarea
                  className="w-full min-h-[120px] border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 rounded-xl"
                  placeholder={t('sendMessage.reengagement.instructionsPlaceholder', 'Ex: Use tom amigável e casual. Destaque a promoção de 10% válida até sexta. Não mencione o concorrente X.')}
                  value={block?.data?.chatReengagementInstructions || ''}
                  onChange={(e) => {
                    changeBlock({ name: "chatReengagementInstructions", value: e.target.value });
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('sendMessage.reengagement.instructionsHint', 'Deixe em branco para usar o comportamento padrão da IA.')}
                </p>
              </div>
            </div>
          )}

          {block?.data?.chatTypeMessage === "audio" && (
            <div className="mt-4">
              {recording ? (
                <div className="flex flex-col gap-2">
                  <div className="flex flex-row items-center gap-2">
                    <p>{t('sendMessage.audio.recordingInProgress', 'Gravação em andamento')}</p>
                    <CircularProgress size={15} />
                    <p className={mostrarAlerta ? "text-red-500" : ""}>
                      {Math.floor(tempoRestante / 60)}:{(tempoRestante % 60).toString().padStart(2, '0')}
                    </p>
                    {mostrarAlerta && (
                      <p className="text-red-500">{t('sendMessage.audio.timeRunningOut', '(Tempo acabando!)')}</p>
                    )}
                  </div>
                  <div className="flex flex-row gap-2">
                    <IconButton
                      aria-label="cancelRecording"
                      component="span"
                      fontSize="large"
                      disabled={loadingAudio}
                      onClick={handleCancelAudio}
                    >
                      <Iconify icon="mdi:cancel" />
                    </IconButton>
                    {loadingAudio ? (
                      <div>
                        <CircularProgress />
                      </div>
                    ) : (
                      <RecordingTimer />
                    )}
                    <IconButton
                      aria-label="sendRecordedAudio"
                      component="span"
                      onClick={pararGravacao}
                      disabled={loadingAudio}
                    >
                      <Iconify icon="mdi:send" />
                    </IconButton>
                  </div>
                </div>
              ) : (
                <div className="flex flex-row gap-2">
                  {!audioGravado && (
                    <Button
                      disabled={loadingAudio}
                      onClick={handleStartRecording}
                      className="flex flex-row items-center gap-2"
                    >
                      <Iconify icon="mdi:microphone" />
                      {t('sendMessage.audio.recordButton', 'Gravar audio')}
                    </Button>
                  )}
                  {block?.data?.chatAudioMessage && (
                    <div className="flex flex-row gap-2 items-center">
                      {block?.data?.chatAudioMessage?.location ? (
                        <audio controls>
                          <source src={block?.data?.chatAudioMessage?.location} type="audio/mp3" />
                          {t('sendMessage.audio.notSupported', 'Seu navegador não suporta a reprodução de áudio.')}
                        </audio>
                      ) : (
                        <p>{t('sendMessage.audio.genericError', 'erro')}</p>
                      )}
                      <IconButton
                        onClick={() => setAudioGravado(null)}
                        variant='outlined'
                        color="error"
                      >
                        <Iconify icon="material-symbols:delete" />
                      </IconButton>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {loadingUpload && (
            <div className="w-full h-3">
              <LinearProgress />
            </div>
          )}

          <Divider className="my-4 border-dashed" />

          {isWhatsAppBusiness && (
            <>
              <div className="flex items-center gap-2 mt-4">
                <Switch
                  checked={block?.data?.useTemplate || false}
                  onCheckedChange={(checked) => {
                    changeBlock({ name: "useTemplate", value: checked });
                    if (checked) {
                      buscarTemplates();
                    } else {
                      changeBlock({ name: "templateSelecionado", value: null });
                      changeBlock({ name: "templateVariaveis", value: {} });
                    }
                  }}
                />
                <div className="flex items-center gap-1">
                  <label className={`text-sm font-medium ${templateObrigatorio ? 'text-gray-500' : ''}`}>
                    {t('sendMessage.template.useLabel', 'Usar Template')}
                  </label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span tabIndex={0} className="flex items-center cursor-help">
                          <Iconify icon="mdi:information-outline" width={18} className="text-slate-500" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" align="start" className="max-w-[280px]">
                        <p className="text-xs">
                          {templateObrigatorio
                            ? t('sendMessage.template.requiredTooltip', 'Como há canais de WhatsApp Business configurados, o uso de template é obrigatório para enviar mensagens fora da janela de 24 horas.')
                            : t('sendMessage.template.infoTooltip', 'Os templates do WhatsApp são mensagens pré-aprovadas que permitem iniciar conversas fora da janela de 24 horas. Quando ativado, você poderá selecionar um template para enviar mensagens mesmo sem uma conversa ativa com o cliente. Caso o chat esteja dentro da janela de 24 horas, o template será ignorado.')}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>

              {templateObrigatorio && (
                <div className="mt-2">
                  <AlertInfo
                    title={t('sendMessage.template.requiredTitle', 'Template Obrigatório')}
                    description={t('sendMessage.template.requiredDescription', 'Como há canais de WhatsApp Business configurados, o uso de template é obrigatório para envios fora da janela de 24 horas.')}
                  />
                </div>
              )}

              {(block?.data?.useTemplate || templateObrigatorio) && (
                <div className="mt-4 p-3 sm:p-4 bg-amber-50 border-2 border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <Iconify icon="mdi:alert-circle" className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-amber-900 mb-2">
                        {t('sendMessage.template.channelSelectedTitle', 'Canal de Whatsapp Business selecionado')}
                      </h4>
                      <p className="text-xs text-amber-800 mb-2">
                        {isWhatsAppBusiness && templateObrigatorio
                          ? (block?.data?.chatTypeMessage === "reengagement"
                            ? t('sendMessage.template.reengagementHint', 'Selecione o template para envios fora da janela de 24 horas. Dentro da janela, a IA gerará a mensagem de reengajamento automaticamente.')
                            : t('sendMessage.template.requiredBothHint', 'Preencha o template selecionado e o campo de mensagem abaixo. O template será usado para mensagens fora da janela de 24 horas, e a mensagem será usada dentro da janela de 24 horas.'))
                          : t('sendMessage.template.optionalHint', 'Preencha o template selecionado e o campo de mensagem abaixo. Caso o chat esteja dentro da janela de 24 horas, o template será ignorado.')}
                      </p>
                      <div className="flex flex-col sm:flex-row flex-wrap gap-2 mt-2">
                        <div className="flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium w-full sm:w-auto">
                          <Iconify icon="mdi:clock-outline" className="w-3 h-3 flex-shrink-0" />
                          <span>{t('sendMessage.template.outsideWindowBadge', 'Template: Fora da janela de 24h')}</span>
                        </div>
                        <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium w-full sm:w-auto">
                          <Iconify icon="mdi:clock-check" className="w-3 h-3 flex-shrink-0" />
                          <span>{t('sendMessage.template.insideWindowBadge', 'Mensagem: Dentro da janela de 24h')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className={`mt-4 grid grid-cols-1 gap-4 ${block?.data?.useTemplate && block?.data?.chatTypeMessage === "text" ? 'lg:grid-cols-1 xs:grid-cols-1 sm:grid-cols-1 xl:grid-cols-2 md:grid-cols-1' : ''}`}>
                {block?.data?.useTemplate && (
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Label className="block text-sm font-medium">
                          {t('sendMessage.template.label', 'Template do WhatsApp')}
                          {(block?.data?.useTemplate || templateObrigatorio) && (
                            <span className="text-red-600 ml-1">*</span>
                          )}
                        </Label>
                        {block?.data?.templateSelecionado ? (
                          <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <Iconify icon="mdi:check" className="w-3 h-3 text-white" />
                          </div>
                        ) : (
                          <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <Iconify icon="mdi:alert" className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="mb-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-md text-xs font-medium border border-purple-200 w-full sm:w-auto">
                          <Iconify icon="mdi:clock-outline" className="w-3 h-3 flex-shrink-0" />
                          <span className="whitespace-nowrap">{t('sendMessage.template.windowMessagePrefix', 'Para mensagens')} <strong>{t('sendMessage.template.windowOutside', 'fora')}</strong> {t('sendMessage.template.windowSuffix', 'da janela de 24 horas')}</span>
                        </div>
                      </div>
                      <Select
                        value={block?.data?.templateSelecionado?.templateId || "none"}
                        onValueChange={handleSelecionarTemplate}
                        disabled={loadingTemplates}
                      >
                        <SelectTrigger className={`w-full ${(block?.data?.useTemplate || templateObrigatorio) && !block?.data?.templateSelecionado ? 'border-red-300 focus:border-red-500 ring-red-200' : block?.data?.templateSelecionado ? 'border-purple-300 focus:border-purple-500' : ''}`}>
                          {loadingTemplates ? (
                            <div className="flex items-center gap-2">
                              <CircularProgress size={16} />
                              <span className="text-sm text-gray-500">{t('sendMessage.template.loading', 'Carregando templates...')}</span>
                            </div>
                          ) : (
                            <SelectValue placeholder={t('sendMessage.template.selectPlaceholder', 'Selecione um template')}>
                              {block?.data?.templateSelecionado ? block.data.templateSelecionado.name : null}
                            </SelectValue>
                          )}
                        </SelectTrigger>
                        <SelectContent className="max-h-[400px]">
                          <SelectItem value="none">
                            <div className="flex items-center gap-2">
                              <Iconify icon="mdi:close-circle" className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-500">{t('sendMessage.template.noneOption', 'Nenhum template selecionado')}</span>
                            </div>
                          </SelectItem>
                          {templates.length === 0 ? (
                            <div className="px-2 py-6 text-center">
                              <Iconify icon="mdi:file-document-remove-outline" className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                              <p className="text-sm text-gray-500">{t('sendMessage.template.noneAvailable', 'Nenhum template disponível')}</p>
                            </div>
                          ) : (
                            templates.map((template) => {
                              const comps = normalizarComponents(template?.components || []);
                              const conteudo = comps?.find(c => c.type === 'BODY')?.text || '';
                              const temVariaveis = conteudo.match(/\{\{\d+\}\}/g)?.length > 0;
                              const templateName = template.name || template.templateName || t('sendMessage.template.noName', 'Sem nome');
                              const categoria = transformCategoryTemplateWhatsApp(template?.category) || 'N/A';
                              const isSelected = block?.data?.templateSelecionado?.templateId === (template.templateId || template._id);
                              return (
                                <SelectItem
                                  key={template.templateId || template._id}
                                  value={template.templateId || template._id}
                                >
                                  <div className="flex items-start gap-3 w-full py-1">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs flex-shrink-0 ${isSelected ? 'bg-gradient-to-br from-purple-500 to-purple-600' : 'bg-gray-400'}`}>
                                      📄
                                    </div>
                                    <div className="flex-1 min-w-0 space-y-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-sm text-gray-900">
                                          {templateName}
                                        </span>
                                        {isSelected && (
                                          <Iconify icon="mdi:check-circle" className="w-4 h-4 text-green-500" />
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                                          {categoria}
                                        </span>
                                        {temVariaveis && (
                                          <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-600 rounded">
                                            {t('sendMessage.template.hasVariablesBadge', 'Com variáveis')}
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-xs text-gray-500 truncate">
                                        {conteudo || t('sendMessage.template.noContent', 'Sem conteúdo')}
                                      </p>
                                    </div>
                                  </div>
                                </SelectItem>
                              );
                            })
                          )}
                        </SelectContent>
                      </Select>
                      {!block?.data?.templateSelecionado && (block?.data?.useTemplate || templateObrigatorio) && (
                        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                          <Iconify icon="mdi:alert-circle" className="w-3 h-3" />
                          {t('sendMessage.template.selectPlaceholder', 'Selecione um template')}
                        </p>
                      )}
                    </div>

                    {block?.data?.templateSelecionado && (
                      <div className="border-2 border-purple-200 rounded-lg overflow-hidden bg-white shadow-sm">
                        <div className="bg-gradient-to-r from-purple-50 to-purple-100 px-4 py-3 border-b border-purple-200">
                          <div className="flex items-start sm:items-center justify-between gap-2">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center text-white text-lg shadow-sm flex-shrink-0">
                                📄
                              </div>
                              <div className="min-w-0 flex-1">
                                <h3 className="font-semibold text-sm text-gray-900 truncate">
                                  {block.data.templateSelecionado.name}
                                </h3>
                                <p className="text-xs text-gray-600 mt-0.5 truncate">
                                  {t('sendMessage.template.idLabel', 'ID: {{id}}', { id: block.data.templateSelecionado.templateId })}
                                </p>
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                changeBlock({ name: "templateSelecionado", value: null });
                                changeBlock({ name: "templateVariaveis", value: {} });
                              }}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0 flex-shrink-0"
                            >
                              <Iconify icon="mdi:delete" className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="p-4 space-y-4 max-h-[600px] overflow-y-auto">
                          {block?.data?.templateSelecionado?.conteudo && (
                            <div>
                              <div className="bg-gray-100 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                                      <div className="text-sm text-gray-800 leading-relaxed">
                                        {block?.data?.templateVariaveis && Object.keys(block.data.templateVariaveis).length > 0 ? (
                                          <div className="space-y-1">
                                            {criarPreviewComCamposTemplate(block.data.templateSelecionado.conteudo, block.data.templateVariaveis)}
                                          </div>
                                        ) : (
                                          <span className="whitespace-pre-wrap">{block.data.templateSelecionado.conteudo}</span>
                                        )}
                                      </div>
                                      <div className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                                        <Iconify icon="mdi:clock-outline" className="w-3 h-3" />
                                        {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {block?.data?.templateVariaveis && Object.keys(block.data.templateVariaveis).length > 0 && (
                            <div>
                              <div className="flex items-center gap-2 mb-3">
                                <div className={`w-6 h-6 ${Object.values(block.data.templateVariaveis).some(v => !String(v || '').trim()) ? 'bg-purple-500' : 'bg-green-500'} rounded-full flex items-center justify-center`}>
                                  <span className="text-white text-xs">✓</span>
                                </div>
                                <h4 className="text-sm font-medium text-gray-900">
                                  {t('sendMessage.template.variablesTitle', 'Variáveis do Template')}
                                </h4>
                              </div>
                              <div className="space-y-3">
                                {Object.keys(block.data.templateVariaveis)
                                  .sort((a, b) => parseInt(a) - parseInt(b))
                                  .map((variavelKey) => (
                                    <div key={variavelKey} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="inline-flex items-center justify-center w-6 h-6 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                                          {variavelKey}
                                        </span>
                                        <Label className="text-xs font-medium text-gray-700">
                                          {t('sendMessage.template.variableLabel', 'Variável {{key}}', { key: variavelKey })}
                                        </Label>
                                        <span className="text-xs text-gray-400 font-mono">
                                          {`{{${variavelKey}}}`}
                                        </span>
                                      </div>
                                      <div className="flex flex-col sm:flex-row gap-2">
                                        <Input
                                          value={block.data.templateVariaveis[variavelKey] || ''}
                                          onChange={(e) => handleAtualizarVariavelTemplate(variavelKey, e.target.value)}
                                          placeholder={t('sendMessage.template.variableValuePlaceholder', 'Digite o valor para a variável {{variable}}', { variable: `{{${variavelKey}}}` })}
                                          className="bg-white border-purple-200 focus:border-purple-500 focus:ring-purple-500 flex-1"
                                        />
                                        <Popover>
                                          <PopoverTrigger asChild>
                                            <Button
                                              type="button"
                                              variant="outline"
                                              size="icon"
                                              className="h-8 w-full sm:w-8 flex-shrink-0 border-purple-200 hover:bg-purple-50"
                                            >
                                              <Iconify icon="hugeicons:code" className="w-4 h-4 text-purple-600" />
                                            </Button>
                                          </PopoverTrigger>
                                          <PopoverContent className="w-[280px]" align="end">
                                            <h5 className="text-sm font-semibold mb-3">{t('sendMessage.template.insertVariablesTitle', 'Inserir variáveis')}</h5>
                                            <div className="space-y-2">
                                              {variaveisDisponiveis.map((variable, index) => (
                                                <Button
                                                  key={index}
                                                  type="button"
                                                  variant="ghost"
                                                  className="w-full justify-start text-left h-auto py-2 px-3 hover:bg-purple-50"
                                                  onClick={() => handleInserirVariavelNoCampo(variavelKey, variable.name)}
                                                >
                                                  <div className="flex flex-col items-start gap-1 w-full">
                                                    <span className="font-semibold text-sm text-gray-900">{variable.name}</span>
                                                    <span className="text-xs text-gray-500">{variable.desc}</span>
                                                  </div>
                                                </Button>
                                              ))}
                                            </div>
                                          </PopoverContent>
                                        </Popover>
                                      </div>
                                      {!block.data.templateVariaveis[variavelKey] && (
                                        <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                                          <Iconify icon="mdi:alert-circle" className="w-3 h-3" />
                                          {t('sendMessage.template.variableRequired', 'Esta variável precisa ser preenchida')}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                              </div>
                              <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
                                <div className="flex items-start gap-2">
                                  <Iconify icon="mdi:information" className="w-5 h-5 text-blue-600 mt-0.5" />
                                  <div className="flex-1">
                                    <p className="text-xs text-blue-800 font-medium">
                                      {Object.values(block.data.templateVariaveis).some(v => !String(v || '').trim())
                                        ? t('sendMessage.template.fillAllVariables', 'Preencha todas as variáveis para completar o template')
                                        : t('sendMessage.template.allVariablesFilled', 'Todas as variáveis foram preenchidas ✓')}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {(!block?.data?.templateVariaveis || Object.keys(block.data.templateVariaveis).length === 0) && (
                            <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                              <div className="flex items-center gap-2">
                                <Iconify icon="mdi:check-circle" className="w-5 h-5 text-green-600" />
                                <p className="text-xs text-green-800 font-medium">
                                  {t('sendMessage.template.noVariablesReady', 'Este template não possui variáveis e está pronto para uso')}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {block?.data?.chatTypeMessage === "text" && (
                  <div className={block?.data?.useTemplate ? '' : 'lg:col-span-2'}>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <label className="block text-sm font-medium">
                        {t('sendMessage.text.label', 'Mensagem de Texto')}
                        {!(isWhatsAppBusiness && templateObrigatorio) && <span className="text-red-600 ml-1">*</span>}
                      </label>
                      {block?.data?.chatTextMessage?.trim() ? (
                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <Iconify icon="mdi:check" className="w-3 h-3 text-white" />
                        </div>
                      ) : (
                        <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <Iconify icon="mdi:alert" className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="mb-2">
                      <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-xs font-medium border border-blue-200 w-full sm:w-auto">
                        <Iconify icon="mdi:clock-check" className="w-3 h-3 flex-shrink-0" />
                        <span className="whitespace-nowrap">{t('sendMessage.template.windowMessagePrefix', 'Para mensagens')} <strong>{t('sendMessage.template.windowInside', 'dentro')}</strong> {t('sendMessage.template.windowSuffix', 'da janela de 24 horas')}</span>
                      </div>
                    </div>
                    {isChatFlow ? (
                      <VariableInput
                        name="chatTextMessage"
                        value={block?.data?.chatTextMessage || ''}
                        onChange={(e) => {
                          let event = { name: "chatTextMessage", value: e.target.value };
                          changeBlock(event);
                        }}
                        variables={availableVariables}
                      />
                    ) : (
                      <Textarea
                        className={`w-full min-h-[200px] ${!block?.data?.chatTextMessage?.trim() && !(isWhatsAppBusiness && templateObrigatorio) ? 'border-red-300 focus:border-red-500' : ''}`}
                        placeholder={t('sendMessage.text.placeholder', 'Escreva sua mensagem aqui...')}
                        value={block?.data?.chatTextMessage || ''}
                        onChange={(e) => {
                          let event = { name: "chatTextMessage", value: e.target.value };
                          changeBlock(event);
                        }}
                      />
                    )}

                    {!block?.data?.chatTextMessage?.trim() && !(isWhatsAppBusiness && templateObrigatorio) && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <Iconify icon="mdi:alert-circle" className="w-3 h-3" />
                        {t('sendMessage.text.fillRequired', 'Preencha o campo de mensagem')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {!isWhatsAppBusiness && block?.data?.chatTypeMessage === "text" && (
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-sm font-medium">
                  {t('sendMessage.text.label', 'Mensagem de Texto')}
                  <span className="text-red-600 ml-1">*</span>
                </label>
                {block?.data?.chatTextMessage?.trim() ? (
                  <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                    <Iconify icon="mdi:check" className="w-3 h-3 text-white" />
                  </div>
                ) : (
                  <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                    <Iconify icon="mdi:alert" className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>
              {
                isChatFlow ? (
                  <VariableInput
                    multiline
                    rows={5}
                    name="chatTextMessage"
                    value={block?.data?.chatTextMessage || ''}
                    onChange={(e) => {
                      let event = { name: "chatTextMessage", value: e.target.value };
                      changeBlock(event);
                    }}
                    variables={availableVariables}
                  />
                ) : (
                  <Textarea
                    className={`w-full min-h-[200px] ${!block?.data?.chatTextMessage?.trim() ? 'border-red-300 focus:border-red-500' : ''}`}
                    placeholder={t('sendMessage.text.placeholder', 'Escreva sua mensagem aqui...')}
                    value={block?.data?.chatTextMessage || ''}
                    onChange={(e) => {
                      let event = { name: "chatTextMessage", value: e.target.value };
                      changeBlock(event);
                    }}
                  />
                )}
              {!block?.data?.chatTextMessage?.trim() && (
                <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                  <Iconify icon="mdi:alert-circle" className="w-3 h-3" />
                  {t('sendMessage.text.fillRequired', 'Preencha o campo de mensagem')}
                </p>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 mt-8">
            <Switch
              disabled={block?.data?.chatTypeMessage === "resume"}
              checked={block?.data?.chatTypeMessage === "resume" ? true : block?.data?.sendDirectNumber}
              onCheckedChange={(checked) => changeBlock({ name: "sendDirectNumber", value: checked })}
            />
            <label className="text-sm font-medium">{t('sendMessage.directNumber.toggleLabel', 'Enviar para número específico')}</label>
          </div>

          {(block?.data?.chatTypeMessage !== "resume" || onlyMsgText) && (
            <div className="flex items-center gap-2 mt-4">
              <Switch
                checked={block?.data?.scheduleMessage}
                onCheckedChange={(checked) => {
                  changeBlock({ name: 'scheduleMessage', value: checked });
                  if (checked) {
                    changeBlock({ name: 'timeSchedule', value: 1 });
                    changeBlock({ name: 'scheduleUnit', value: 'dias' });
                  }
                }}
              />
              <label className="text-sm font-medium">{t('sendMessage.schedule.toggleLabel', 'Agendar envio da mensagem')}</label>
            </div>
          )}

          {block?.data?.scheduleMessage && (
            <div className="flex flex-row items-center gap-2 mt-2">
              <label className="text-sm">{t('sendMessage.schedule.afterLabel', 'Enviar após')}</label>
              <input
                type="number"
                min={block?.data?.scheduleUnit === 'minutos' ? 5 : 1}
                className="border rounded px-2 py-1 w-20"
                value={block.data.timeSchedule}
                onChange={e => {
                  let value = e.target.value;
                  if (block?.data?.scheduleUnit === 'minutos' && Number(value) < 5) {
                    value = 5;
                  }
                  changeBlock({ name: 'timeSchedule', value });
                }}
              />
              <Select
                value={block?.data?.scheduleUnit}
                onValueChange={unidade => {
                  changeBlock({ name: 'scheduleUnit', value: unidade });
                  if (unidade === 'minutos' && block?.data?.timeSchedule < 5) {
                    changeBlock({ name: 'timeSchedule', value: 5 });
                  }
                }}
              >
                <SelectTrigger className="w-28">
                  <SelectValue placeholder={t('sendMessage.schedule.unitPlaceholder', 'Unidade')} />
                </SelectTrigger>
                <SelectContent>
                  {opcoesUnidadeTempo.map((opcao) => (
                    <SelectItem key={opcao.value} value={opcao.value}>{opcao.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {block?.data?.sendDirectNumber && (
            <RowInput
              isRequired={true}
              label={t('labels.phone', { ns: 'common', defaultValue: 'Telefone' })}
              input={
                <div className="w-1/3">
                  <PhoneInput
                    international
                    defaultCountry='BR'
                    style={{ padding: 10, borderRadius: 4, border: '1px solid #ccc' }}
                    placeholder={t('sendMessage.directNumber.placeholder', 'Insira seu numero')}
                    value={block?.data?.directNumber}
                    onChange={value => changeBlock({ name: "directNumber", value: value })}
                  />
                </div>
              }
            />
          )}

          {isChatFlow && (
            <div className="flex items-center gap-2 mt-4">
              <Switch
                checked={block?.data?.waitingQuestion || false}
                onCheckedChange={(checked) => changeBlock({ name: "waitingQuestion", value: checked })}
              />
              <label className="text-sm font-medium">{t('sendMessage.waitingQuestion.label', 'Solicitar resposta')}</label>
            </div>
          )}
        </div>
      </div>

      {(!block?.data?.sendDirectNumber || onlyMsgText) && (
        <div className="mt-8">
          <AlertInfo
            title={t('status.warning', { ns: 'common', defaultValue: 'Atenção' })}
            description={t('sendMessage.noChatWarning', 'Se não houver nenhum chat vinculado, a mensagem não será enviada.')}
          />
        </div>
      )}
    </div>
  );
};

export default CardSendMessage;
